import React, {useState, useEffect, useMemo} from "react";
import GoogleMapReact from "google-map-react";
import apiKey from "../../../../_core/apiKey";
import {decodePolyline} from "../../../../utils/helpers";
import {useTranslation} from "react-i18next";
import {
    StyledMapContainer,
    CustomMarker,
    TooltipBox,
    MarkerContainer
} from "./CalculatedTransferMapTab.styled";
import {HomeOutlined} from "@ant-design/icons";

const DEPOT_COLOR = "#A2FF00";

const MarkerComponent = ({text, color, number, isDepot}) => (
    <MarkerContainer>
        {isDepot ? (
            <CustomMarker color={DEPOT_COLOR} isDepot>
                <HomeOutlined style={{fontSize: "16px", color: "white"}} />
            </CustomMarker>
        ) : (
            <CustomMarker color={color}>{number}</CustomMarker>
        )}
        <TooltipBox className="tooltip" color={isDepot ? DEPOT_COLOR : color}>
            {text}
        </TooltipBox>
    </MarkerContainer>
);

const CalculatedTransferMapTab = ({encodedPolylines}) => {
    const [map, setMap] = useState(null);
    const [maps, setMaps] = useState(null);
    const [drawnRoutes, setDrawnRoutes] = useState([]);
    const center = {lat: 41.11, lng: 29.11};
    const {i18n} = useTranslation();

    const generateCourierColors = useMemo(() => {
        if (!encodedPolylines?.lastMile?.activities) return [];

        const courierCount = encodedPolylines.lastMile.activities.length;
        const colors = [];

        for (let i = 0; i < courierCount; i++) {
            const hue = (360 / courierCount) * i;
            colors.push(`hsl(${hue}, 60%, 45%)`);
        }

        return colors;
    }, [encodedPolylines?.lastMile?.activities]);

    const renderPolylines = (map, maps) => {
        if (!map || !maps || !encodedPolylines?.lastMile?.points_encoded?.length) return;

        drawnRoutes.forEach(poly => poly.setMap(null));
        let tempDrawnRoutes = [];

        encodedPolylines.lastMile.points_encoded.forEach((pointGroup, groupIndex) => {
            const courierColor = generateCourierColors[groupIndex];

            pointGroup.forEach(encodedPoint => {
                if (!encodedPoint) return;

                const path = decodePolyline(encodedPoint);

                const polylineBorder = new maps.Polyline({
                    path: path,
                    strokeColor: "#000000",
                    strokeWeight: 5,
                    strokeOpacity: 0.5
                });

                const polyline = new maps.Polyline({
                    path: path,
                    strokeColor: courierColor,
                    strokeWeight: 3,
                    strokeOpacity: 1
                });

                polylineBorder.setMap(map);
                polyline.setMap(map);

                tempDrawnRoutes.push(polylineBorder, polyline);
            });
        });

        setDrawnRoutes(tempDrawnRoutes);
    };

    const fitBounds = ({map, encodedPolylines}) => {
        if (!map || !encodedPolylines?.lastMile?.activities) return;

        const bounds = new window.google.maps.LatLngBounds();

        encodedPolylines.lastMile.activities.forEach(activityGroup => {
            activityGroup.forEach(activity => {
                bounds.extend({
                    lat: activity.address.lat,
                    lng: activity.address.lon
                });
            });
        });

        map.fitBounds(bounds);
    };

    useEffect(() => {
        if (map && maps) {
            renderPolylines(map, maps);
            fitBounds({map, encodedPolylines});
        }
    }, [map, maps, encodedPolylines]);

    return (
        <StyledMapContainer mode={"relative"} data-testid="map">
            <GoogleMapReact
                onGoogleApiLoaded={({map, maps}) => {
                    setMap(map);
                    setMaps(maps);
                    renderPolylines(map, maps);
                }}
                heatmapLibrary={true}
                center={center}
                bootstrapURLKeys={{
                    key: apiKey,
                    language: i18n.resolvedLanguage || "tr"
                }}
                yesIWantToUseGoogleMapApiInternals
                defaultZoom={13}
                options={{
                    fullscreenControl: false,
                    gestureHandling: "greedy",
                    styles: [
                        {featureType: "poi", elementType: "labels", stylers: [{visibility: "off"}]}
                    ]
                }}
            >
                {encodedPolylines?.lastMile?.activities.map((activityGroup, groupIndex) => {
                    const courierColor = generateCourierColors[groupIndex];
                    let serviceCounter = 1;

                    return activityGroup.map((activity, activityIndex) => {
                        const isDepot = activity.type === "start";
                        let markerNumber;

                        if (isDepot) {
                            markerNumber = "";
                        } else {
                            markerNumber = serviceCounter.toString();
                            serviceCounter++;
                        }

                        return (
                            <MarkerComponent
                                key={`${activity.location_id}-${activityIndex}`}
                                lat={activity.address.lat}
                                lng={activity.address.lon}
                                text={isDepot ? "Depo" : JSON.parse(activity.id).name}
                                color={courierColor}
                                number={markerNumber}
                                isDepot={isDepot}
                            />
                        );
                    });
                })}
            </GoogleMapReact>
        </StyledMapContainer>
    );
};

export default CalculatedTransferMapTab;
